import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const navItems = [{ to: "/admin/nia-prompt", label: "Nia's Brain", icon: <Brain size={18} weight="duotone" />, testId: "nav-admin-nia-prompt" }];

export default function AdminNiaPromptPage() {
  const confirmEdit = useEditConfirm();
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusBusy, setStatusBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/nia-system-prompt")
      .then(({ data }) => {
        setPrompt(data.prompt || "");
        setDraft(data.prompt || "");
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  const loadStatus = () => {
    setStatusLoading(true);
    api.get("/admin/nia-whatsapp-status")
      .then(({ data }) => setWhatsappEnabled(data.enabled))
      .catch(() => {})
      .finally(() => setStatusLoading(false));
  };

  useEffect(() => {
    load();
    loadStatus();
  }, []);

  const toggleWhatsapp = async () => {
    const next = !whatsappEnabled;
    if (!(await confirmEdit(next ? "Turn Nia's WhatsApp replies back ON" : "Turn Nia's WhatsApp replies OFF"))) return;
    setStatusBusy(true);
    try {
      const { data } = await api.put("/admin/nia-whatsapp-status", { enabled: next });
      setWhatsappEnabled(data.enabled);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setStatusBusy(false);
    }
  };

  const save = async () => {
    if (!draft.trim()) {
      toast.error("Prompt cannot be empty");
      return;
    }
    if (!(await confirmEdit("Nia's System Prompt"))) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/nia-system-prompt", { prompt: draft });
      setPrompt(data.prompt);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const revert = () => setDraft(prompt);
  const dirty = draft !== prompt;

  return (
    <DashboardLayout navItems={navItems} moduleName="Nia's Brain" moduleBackPath="/admin">
      <div data-testid="admin-nia-prompt-page" className="max-w-4xl">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2" title="Nia's system prompt is the master instructions that shape her personality, product knowledge, and rules across every conversation.">Nia's System Prompt</h1>
        <p className="text-igc-muted text-sm mb-6">
          This is the exact text that governs Nia's tone, product knowledge, commission numbers, escalation rules, and program details across every
          conversation with every rep and manager. Edit it here and save — the very next conversation any rep starts with Nia picks it up automatically.
          No republish, no code change, no downtime.
        </p>

        <div className="bg-igc-surface border border-igc-gold/15 p-6 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="nia-whatsapp-status-card">
          <div>
            <p className="text-igc-cream text-sm font-medium">Nia's WhatsApp Auto-Replies</p>
            <p className="text-igc-muted text-xs mt-1">
              {whatsappEnabled
                ? "Nia answers Wes's free-text WhatsApp messages. APPROVE / REJECT / REPLENISH commands always work regardless of this setting."
                : "Nia is OFF — free-text WhatsApp messages from Wes get no AI reply. APPROVE / REJECT / REPLENISH commands still work."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs uppercase tracking-wide ${whatsappEnabled ? "text-igc-success" : "text-igc-danger"}`} data-testid="nia-whatsapp-status-label">
              {statusLoading ? "..." : whatsappEnabled ? "ON" : "OFF"}
            </span>
            <Switch data-testid="nia-whatsapp-toggle" checked={whatsappEnabled} disabled={statusLoading || statusBusy} onCheckedChange={toggleWhatsapp} />
          </div>
        </div>

        {loading && <p className="text-igc-muted text-sm" data-testid="nia-prompt-loading">Loading Nia's current prompt...</p>}

        {!loading && error && (
          <p className="text-igc-danger text-sm" data-testid="nia-prompt-error-state">Couldn't load Nia's prompt. Please refresh the page or try again shortly.</p>
        )}

        {!loading && !error && (
          <div className="bg-igc-surface border border-igc-gold/15 p-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest text-igc-muted">Full System Prompt ({draft.length.toLocaleString()} characters)</label>
              {dirty && <span className="text-[10px] uppercase tracking-wide text-amber-500" data-testid="nia-prompt-unsaved-indicator">Unsaved changes</span>}
            </div>
            <Textarea
              data-testid="nia-prompt-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={26}
              title="This is the exact text Nia follows in every conversation. Edit carefully — changes go live the moment you save, with no code deploy needed."
              className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-sm font-mono leading-relaxed resize-y"
            />
            <div className="flex gap-3 mt-4">
              <Button
                data-testid="nia-prompt-save-button"
                onClick={save}
                disabled={saving || !dirty}
                title="Saves this prompt and makes it live immediately for every new Nia conversation."
                className="bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-40"
              >
                {saving ? "Saving..." : "Save & Go Live"}
              </Button>
              <Button
                data-testid="nia-prompt-revert-button"
                onClick={revert}
                disabled={saving || !dirty}
                title="Discards your unsaved edits and restores the last saved version."
                className="bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70 disabled:opacity-40"
              >
                Revert Changes
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
